/**
 * PushInitializer — AI v2 Web Push registration flow (F3-T4)
 *
 * 1. Registra /sw.js como service worker
 * 2. Solicita permiso Notification (silencioso si ya concedido/denegado)
 * 3. Suscribe al PushManager con VAPID public key (de /api/auth/me o env)
 * 4. Upserta la suscripción vía POST /api/ai/push-subscribe
 *
 * Debe montarse sólo cuando VITE_AI_V2==='true' (el padre aplica el guard)
 * Y el usuario está autenticado.
 *
 * VAPID public key:
 *   - Preferido: VITE_VAPID_PUBLIC_KEY env var (inyectada por Amplify)
 *   - Fallback: backend /api/auth/me.vapid_public_key — no implementado aún
 *     (retorna null en F1). Si falta, no registra suscripción.
 */

import { useEffect } from 'react';
import { apiFetch } from '@/utils/api';
import { VAPID_PUBLIC_KEY as FALLBACK_VAPID_KEY } from '@/lib/featureFlags';

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Sprint 6 T9 — fetch VAPID key from /api/auth/config instead of hardcoding.
 * Falls back to the bundled constant if the endpoint is unreachable so push
 * still works on a frozen backend; rotating the key server-side is now a
 * zero-redeploy operation.
 */
async function resolveVapidKey() {
  try {
    const cfg = await apiFetch('/api/auth/config');
    if (cfg?.vapidPublicKey) return cfg.vapidPublicKey;
  } catch {
    /* fall through to bundled fallback */
  }
  return FALLBACK_VAPID_KEY;
}

export default function PushInitializer({ enabled = true }) {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    let cancelled = false;

    const register = async () => {
      try {
        const vapidPublicKey = await resolveVapidKey();
        if (cancelled || !vapidPublicKey) return;

        // '/sw.js' a secas resolvería contra la raíz del dominio (la landing,
        // no la SPA) ahora que la app vive bajo import.meta.env.BASE_URL
        // ("/app/"). Registrarlo ahí también le da el scope correcto
        // ("/app/") por defecto, en vez del "/" que tenía antes.
        const reg = await navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`);
        if (cancelled) return;

        // Permiso (sólo prompt si default; si ya denegado no insistimos)
        if (Notification.permission === 'default') {
          const res = await Notification.requestPermission();
          if (res !== 'granted' || cancelled) return;
        } else if (Notification.permission !== 'granted') {
          return;
        }

        // Suscribir (si ya existe, getSubscription devuelve la actual)
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
          });
        }
        if (cancelled) return;

        const key = sub.getKey ? sub.getKey('p256dh') : null;
        const auth = sub.getKey ? sub.getKey('auth') : null;
        if (!key || !auth) return;

        await apiFetch('/api/ai/push-subscribe', {
          method: 'POST',
          body: JSON.stringify({
            endpoint: sub.endpoint,
            p256dh: btoa(String.fromCharCode(...new Uint8Array(key))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
            auth: btoa(String.fromCharCode(...new Uint8Array(auth))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
          }),
        });
      } catch {
        /* Silent fail — long-poll sigue funcionando como fallback */
      }
    };

    register();
    return () => { cancelled = true; };
  }, [enabled]);

  return null;
}
